import React from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from 'recharts';
import { useTranslation } from 'react-i18next';

// ── Consumer data (unchanged) ──────────────────────────────────────────────
const baseConsumption = [
  { name: '00:30', hour: 0, total: 13 },
  { name: '01:45', hour: 1, total: 12 },
  { name: '03:00', hour: 3, total: 11 },
  { name: '04:15', hour: 4, total: 10 },
  { name: '05:30', hour: 5, total: 9 },
  { name: '06:45', hour: 6, total: 15 },
  { name: '08:00', hour: 8, total: 18 },
  { name: '09:15', hour: 9, total: 32 },
  { name: '10:30', hour: 10, total: 54 },
  { name: '11:30', hour: 11, total: 57 },
  { name: '12:30', hour: 12, total: 40 },
  { name: '13:30', hour: 13, total: 50 },
  { name: '14:45', hour: 14, total: 33 },
  { name: '15:45', hour: 15, total: 47 },
  { name: '16:45', hour: 16, total: 30 },
  { name: '17:45', hour: 17, total: 24 },
  { name: '18:45', hour: 18, total: 23 },
  { name: '19:45', hour: 19, total: 26 },
  { name: '21:00', hour: 21, total: 25 },
  { name: '22:15', hour: 22, total: 20 },
  { name: '23:45', hour: 23, total: 12 },
];

const consumerBeforeData = baseConsumption.map(point => ({
  ...point,
  reseau: point.total,
  partage: 0,
}));

const consumerAfterData = baseConsumption.map(point => {
  const isSolarHours = point.hour >= 9 && point.hour <= 17;
  const partagePercent = isSolarHours ? 0.8 : 0;
  return {
    ...point,
    reseau: Math.round(point.total * (1 - partagePercent)),
    partage: Math.round(point.total * partagePercent),
  };
});

// ── Producer solar data (inverted-U bell curve) ────────────────────────────
// Hours 05:00–20:00; peak around 12:00–13:00
const solarProduction = [
  { name: '00:30', hour: 0, production: 0 },
  { name: '01:45', hour: 1, production: 0 },
  { name: '03:00', hour: 3, production: 0 },
  { name: '04:15', hour: 4, production: 0 },
  { name: '05:30', hour: 5, production: 2 },
  { name: '06:45', hour: 6, production: 8 },
  { name: '08:00', hour: 8, production: 22 },
  { name: '09:15', hour: 9, production: 38 },
  { name: '10:30', hour: 10, production: 52 },
  { name: '11:30', hour: 11, production: 62 },
  { name: '12:30', hour: 12, production: 66 },
  { name: '13:30', hour: 13, production: 64 },
  { name: '14:45', hour: 14, production: 55 },
  { name: '15:45', hour: 15, production: 42 },
  { name: '16:45', hour: 16, production: 26 },
  { name: '17:45', hour: 17, production: 12 },
  { name: '18:45', hour: 18, production: 4 },
  { name: '19:45', hour: 19, production: 1 },
  { name: '21:00', hour: 21, production: 0 },
  { name: '22:15', hour: 22, production: 0 },
  { name: '23:45', hour: 23, production: 0 },
];

// "before": all production goes to grid (blue)
const producerBeforeData = solarProduction.map(p => ({
  ...p,
  reseau: p.production,
  partage: 0,
}));

// "after": ~75% of production is shared (green overlay on the blue), rest to grid
const producerAfterData = solarProduction.map(p => ({
  ...p,
  reseau: Math.round(p.production * 0.25),
  partage: Math.round(p.production * 0.75),
}));

// ──────────────────────────────────────────────────────────────────────────

interface ChartProps {
  type: 'before' | 'after';
  role?: 'consumer' | 'producer';
}

export const BeforeAfterChart: React.FC<ChartProps> = ({ type, role = 'consumer' }) => {
  const { t } = useTranslation();

  const data = role === 'producer'
    ? (type === 'before' ? producerBeforeData : producerAfterData)
    : (type === 'before' ? consumerBeforeData : consumerAfterData);

  const yMax = role === 'producer' ? 70 : 60;
  const yTicks = role === 'producer' ? [0, 20, 40, 60, 70] : [0, 15, 30, 45, 60];

  const blueLabel = role === 'producer'
    ? (type === 'before' ? 'Injection réseau (kWh)' : 'Injection réseau résiduelle (kWh)')
    : (type === 'before' ? t('home.diagram.beforeLegend') : undefined);

  const greenLabel = 'Énergie partagée avec la communauté (kWh)';

  return (
    <div className="w-full h-full p-2 sm:p-3" style={{ backgroundColor: 'white', position: 'relative', minHeight: '280px' }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 8, right: 5, left: 0, bottom: 44 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={true} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 9, fill: '#6b7280' }}
            interval={2}
            angle={0}
            textAnchor="middle"
            height={32}
          />
          <YAxis
            domain={[0, yMax]}
            ticks={yTicks}
            tick={{ fontSize: 10, fill: '#6b7280' }}
            width={46}
            label={{ value: role === 'producer' ? 'Production solaire (kWh)' : 'Consommation (kWh)', angle: -90, position: 'insideLeft', offset: 12, fontSize: 11, fill: '#6b7280', dy: 70 }}
          />

          {/* Blue area: grid injection (producer) or grid consumption (consumer) */}
          <Area
            type="monotone"
            dataKey="reseau"
            stroke="#5b8ff9"
            strokeWidth={2}
            fill="#5b8ff9"
            fillOpacity={0.35}
            stackId="1"
          />

          {/* Green area: shared energy (shown only in "after") */}
          {type === 'after' && (
            <Area
              type="monotone"
              dataKey="partage"
              stroke="#22c55e"
              strokeWidth={2}
              fill="#22c55e"
              fillOpacity={0.45}
              stackId="1"
            />
          )}

          <Legend
            verticalAlign="bottom"
            height={44}
            wrapperStyle={{ paddingTop: '6px' }}
            content={() => (
              <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-4 text-xs sm:text-sm px-2">
                {role === 'producer' ? (
                  <div className="flex flex-wrap justify-center gap-4">
                    <span className="flex items-center gap-1.5">
                      <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#5b8ff9', opacity: 0.8 }} />
                      <span style={{ color: '#1a1a1a' }}>{blueLabel}</span>
                    </span>
                    {type === 'after' && (
                      <span className="flex items-center gap-1.5">
                        <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#22c55e', opacity: 0.8 }} />
                        <span style={{ color: '#1a1a1a' }}>{greenLabel}</span>
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="text-center sm:text-left" style={{ color: '#1a1a1a' }}>
                    {type === 'before' ? (
                      (t('home.diagram.beforeLegend') || '').split(t('home.diagram.supplierWord')).map((part, index, array) => (
                        <React.Fragment key={index}>
                          {part}
                          {index < array.length - 1 && (
                            <span style={{ color: '#5b8ff9', fontWeight: 600 }}>
                              {t('home.diagram.supplierWord')}
                            </span>
                          )}
                        </React.Fragment>
                      ))
                    ) : (
                      (() => {
                        const text = t('home.diagram.afterLegend');
                        const producersText = t('home.diagram.brusselsProducers');
                        const supplierText = t('home.diagram.supplierWord');
                        const producersIndex = text.indexOf(producersText);
                        const supplierIndex = text.indexOf(supplierText);
                        if (producersIndex === -1 || supplierIndex === -1) return text;
                        const parts: React.ReactNode[] = [];
                        let currentIndex = 0;
                        if (producersIndex < supplierIndex) {
                          parts.push(text.substring(currentIndex, producersIndex));
                          parts.push(<span key="producers" style={{ color: '#22c55e', fontWeight: 600 }}>{producersText}</span>);
                          currentIndex = producersIndex + producersText.length;
                          parts.push(text.substring(currentIndex, supplierIndex));
                          parts.push(<span key="supplier" style={{ color: '#5b8ff9', fontWeight: 600 }}>{supplierText}</span>);
                          currentIndex = supplierIndex + supplierText.length;
                          parts.push(text.substring(currentIndex));
                        } else {
                          parts.push(text.substring(currentIndex, supplierIndex));
                          parts.push(<span key="supplier" style={{ color: '#5b8ff9', fontWeight: 600 }}>{supplierText}</span>);
                          currentIndex = supplierIndex + supplierText.length;
                          parts.push(text.substring(currentIndex, producersIndex));
                          parts.push(<span key="producers" style={{ color: '#22c55e', fontWeight: 600 }}>{producersText}</span>);
                          currentIndex = producersIndex + producersText.length;
                          parts.push(text.substring(currentIndex));
                        }
                        return parts;
                      })()
                    )}
                  </div>
                )}
              </div>
            )}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};
